import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Nav from '@/components/Nav';
import Footer from '@/components/Footer';
import StickyBottomMarquee, { STICKY_MARQUEE_SPACER_CLASS } from '@/components/StickyBottomMarquee';
import Lightbox from '@/components/Lightbox';
import { api, fileUrl } from '@/lib/api';
import { MotionProvider, useReveal } from '@/lib/motion';
import MaintenancePage from '@/pages/MaintenancePage';

const EAGER_IMAGE_COUNT = 8;
const GALLERY_PAGE_LIMIT = 24;

export default function Gallery() {
  // --- Settings ownership. /public/home is the sole owner of `settings` and
  // `bottomMarquee`. It never touches gallery state. ---
  const [settings, setSettings] = useState({});
  const [bottomMarquee, setBottomMarquee] = useState({});
  const [settingsResolved, setSettingsResolved] = useState(false);
  const [pageError, setPageError] = useState(false);

  // --- Gallery ownership. /public/gallery is the sole owner of `photos`,
  // `nextCursor`, `hasMore`, `loadingMore`, `galleryError`, and `galleryTotal`. ---
  const [photos, setPhotos] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [galleryError, setGalleryError] = useState(false);
  // API-reported total across ALL matching photos, independent of the local
  // loaded slice. `null` until the first successful response.
  const [galleryTotal, setGalleryTotal] = useState(null);

  // --- Year / event filters. `filters` is the server-authoritative data
  // from /public/gallery-filters; selYear === null means All Photos, and
  // selEvent === null within a year means All Events. ---
  const [filters, setFilters] = useState(null);
  const [selYear, setSelYear] = useState(null);
  const [selEvent, setSelEvent] = useState(null);
  // The FIRST photo request waits for filter metadata, so the page can
  // never briefly render unfiltered results and then snap to the default.
  const [filtersReady, setFiltersReady] = useState(false);
  // Every filter change bumps the epoch; a response from an older epoch is
  // discarded so switching filters can never interleave two result sets.
  const filterEpochRef = useRef(0);
  // Ownership token for Load More: only the newest request may release
  // the single-flight lock or clear the loading flag.
  const loadMoreTokenRef = useRef(0);

  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);
  const headerReveal = useReveal();
  const gridReveal = useReveal();

  // Single-flight lock for Load More. State updates are asynchronous, so a
  // rapid double-click can pass a state-only check twice; the ref catches
  // the second call before React has re-rendered.
  const loadInFlightRef = useRef(false);

  // Component-lifetime activity ref. Every asynchronous handler below reads
  // this before touching state, so a response arriving after the component
  // is torn down is discarded silently instead of updating an unmounted
  // component. Declared BEFORE any request effect.
  const activeRef = useRef(false);
  useEffect(() => {
    activeRef.current = true;
    return () => { activeRef.current = false; };
  }, []);

  // Validate a /public/gallery response before use.
  const validateGalleryResponse = useCallback((data) => {
    if (!data || !Array.isArray(data.items)) return false;
    // has_more true but no usable cursor is inconsistent — reject.
    if (data.has_more === true && (typeof data.next_cursor !== 'string' || data.next_cursor.length === 0)) return false;
    return true;
  }, []);

  // ---- SETTINGS request. Fail-closed on error. ----
  const fetchSettings = useCallback(() => {
    let cancelled = false;
    setPageError(false);
    setSettingsResolved(false);
    api.get('/public/home').then((r) => {
      if (cancelled) return;
      if (!activeRef.current) return;
      setSettings(r.data?.settings || {});
      setBottomMarquee(r.data?.settings?.bottom_marquee || {});
      setSettingsResolved(true);
      setPageError(false);
    }).catch(() => {
      if (cancelled) return;
      if (!activeRef.current) return;
      // Fail closed. Do NOT mark settings resolved, do NOT touch gallery
      // state, do NOT render photos. Maintenance status is unknown.
      setPageError(true);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cleanup = fetchSettings();
    return cleanup;
  }, [fetchSettings]);

  const retrySettings = useCallback(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Params for the ACTIVE filter. Load More and Retry must carry exactly
  // what the first page carried, or the server rejects the cursor.
  const filterParams = useCallback(() => {
    const p = { limit: GALLERY_PAGE_LIMIT };
    if (selYear !== null) p.year = selYear;
    if (selEvent) p.event_id = selEvent;
    return p;
  }, [selYear, selEvent]);

  // ---- FILTER DATA. Resolves BEFORE the first photo request: the
  // default view is selected here, so the wrong (unfiltered) results are
  // never fetched, let alone flashed. On failure the controls simply do
  // not render and the gallery explicitly falls back to legacy All
  // Photos. Either way the lightbox is closed and reset. ----
  useEffect(() => {
    let cancelled = false;
    const finish = (data, defaultYear) => {
      setFilters(data);
      setLbOpen(false);
      setLbIndex(0);
      if (defaultYear !== null) {
        setSelYear(defaultYear);
        setSelEvent(null);
      } else {
        setSelYear(null);
        setSelEvent(null);
      }
      setFiltersReady(true);
    };
    api.get('/public/gallery-filters').then((r) => {
      if (cancelled || !activeRef.current) return;
      const d = r.data;
      if (!d || !Array.isArray(d.years)) {
        finish({ years: [], untagged_visible: 0 }, null);
        return;
      }
      // Default: All Photos while any visible photo is untagged (nothing
      // can vanish mid-backfill); latest year + All Events once every
      // visible photo is tagged.
      const useLatest = d.years.length > 0 && d.untagged_visible === 0 && Number.isInteger(d.latest_eligible_year);
      finish(d, useLatest ? d.latest_eligible_year : null);
    }).catch(() => {
      if (cancelled || !activeRef.current) return;
      finish({ years: [], untagged_visible: 0 }, null);
    });
    return () => { cancelled = true; };
  }, []);

  // ---- FIRST-PAGE gallery request. Waits for filter metadata, then
  // reruns whenever the filter changes; the epoch guard discards any
  // response that arrives late. ----
  useEffect(() => {
    if (!filtersReady) return;
    let cancelled = false;
    const epoch = filterEpochRef.current;
    setGalleryError(false);
    api.get('/public/gallery', { params: filterParams() }).then((r) => {
      if (cancelled) return; // stale response discarded (StrictMode-safe)
      if (!activeRef.current) return;
      if (epoch !== filterEpochRef.current) return; // filter changed mid-flight
      const data = r.data;
      if (!validateGalleryResponse(data)) {
        // Preserve whatever is already on screen (nothing on first load).
        setGalleryError(true);
        return;
      }
      setPhotos(data.items);
      setHasMore(!!data.has_more);
      setNextCursor(data.has_more ? data.next_cursor : null);
      setGalleryTotal(Number.isInteger(data.total) ? data.total : null);
    }).catch(() => {
      if (cancelled) return;
      if (!activeRef.current) return;
      if (epoch !== filterEpochRef.current) return;
      // Preserve existing photos and nextCursor. Do NOT clear.
      setGalleryError(true);
    });
    return () => { cancelled = true; };
  }, [filtersReady, validateGalleryResponse, filterParams]);

  // ---- FILTER SELECTION. One atomic reset: close and reset the
  // lightbox, bump the epoch so in-flight responses die, invalidate any
  // in-flight Load More's ownership token, clear list/cursor/total/error
  // and the request lock, then let the first-page effect refetch. ----
  const applyFilter = useCallback((yearVal, eventVal) => {
    if (yearVal === selYear && eventVal === selEvent) return;
    setLbOpen(false);
    setLbIndex(0);
    filterEpochRef.current += 1;
    loadMoreTokenRef.current += 1;
    setPhotos(null);
    setNextCursor(null);
    setHasMore(false);
    setGalleryTotal(null);
    setGalleryError(false);
    loadInFlightRef.current = false;
    setLoadingMore(false);
    setSelYear(yearVal);
    // Changing the year always resets the event to All Events.
    setSelEvent(eventVal);
  }, [selYear, selEvent]);

  // ---- LOAD MORE. Genuinely single-flight (ref + state). ----
  const loadMore = useCallback(() => {
    if (loadInFlightRef.current) return;      // catches rapid double-click
    if (loadingMore) return;
    if (!hasMore) return;
    if (typeof nextCursor !== 'string' || nextCursor.length === 0) return;

    const cursorForThisRequest = nextCursor;  // captured at call time
    const epoch = filterEpochRef.current;     // bound to the active filter
    // Take ownership: only the request holding the newest token may
    // release the lock, so a stale finally can never unlock a newer
    // request or clear its loading state.
    const myToken = loadMoreTokenRef.current + 1;
    loadMoreTokenRef.current = myToken;
    loadInFlightRef.current = true;
    setLoadingMore(true);
    setGalleryError(false);

    api.get('/public/gallery', { params: { ...filterParams(), cursor: cursorForThisRequest } })
      .then((r) => {
        if (!activeRef.current) return;
        if (epoch !== filterEpochRef.current) return; // filter changed mid-flight
        const data = r.data;
        if (!validateGalleryResponse(data)) {
          // Preserve existing photos and existing nextCursor. Do not advance.
          setGalleryError(true);
          return;
        }
        setPhotos((prev) => [...(prev || []), ...data.items]);
        setHasMore(!!data.has_more);
        setNextCursor(data.has_more ? data.next_cursor : null);
        setGalleryTotal(Number.isInteger(data.total) ? data.total : null);
      })
      .catch(() => {
        if (!activeRef.current) return;
        if (epoch !== filterEpochRef.current) return;
        // Preserve existing photos and existing nextCursor. Do not advance.
        setGalleryError(true);
      })
      .finally(() => {
        // Release ONLY if this request still owns the lock. A filter
        // change or a newer Load More bumps the token, and this stale
        // finally then belongs to nobody — it must not unlock the newer
        // request or clear its loading flag.
        if (loadMoreTokenRef.current !== myToken) return;
        loadInFlightRef.current = false;
        // Only clear the loading flag while the component is still
        // active; setState on an unmounted component would warn.
        if (!activeRef.current) return;
        setLoadingMore(false);
      });
  }, [nextCursor, hasMore, loadingMore, validateGalleryResponse, filterParams]);

  // ---- Retry helper for gallery errors. ----
  const retryGallery = useCallback(() => {
    setGalleryError(false);
    if (photos === null) {
      // First-page reissue under the ACTIVE filter and epoch.
      const epoch = filterEpochRef.current;
      api.get('/public/gallery', { params: filterParams() }).then((r) => {
        if (!activeRef.current) return;
        if (epoch !== filterEpochRef.current) return;
        const data = r.data;
        if (!validateGalleryResponse(data)) { setGalleryError(true); return; }
        setPhotos(data.items);
        setHasMore(!!data.has_more);
        setNextCursor(data.has_more ? data.next_cursor : null);
        setGalleryTotal(Number.isInteger(data.total) ? data.total : null);
      }).catch(() => {
        if (!activeRef.current) return;
        if (epoch !== filterEpochRef.current) return;
        setGalleryError(true);
      });
      return;
    }
    // Otherwise reissue the last failed load-more with the preserved cursor.
    loadMore();
  }, [photos, loadMore, validateGalleryResponse, filterParams]);

  // ---- Every hook is declared above this line. Maintenance gate follows. ----
  const maintenance = settings.maintenance || {};
  if (settingsResolved && maintenance.enabled) return <MaintenancePage maintenance={maintenance} />;

  const motion = settings.motion || {};
  const nav = settings.nav || {};
  const footer = settings.footer || {};

  const openLightbox = (idx) => { setLbIndex(idx); setLbOpen(true); };

  // Distinct rendering states — see order Task 2.
  const showPageError = pageError;
  const showLoading =
    !showPageError &&
    (!settingsResolved || (photos === null && !galleryError));
  const showGalleryError = !showPageError && settingsResolved && galleryError;
  const showEmpty = !showPageError && settingsResolved && Array.isArray(photos) && photos.length === 0 && !galleryError;
  const showGrid = !showPageError && settingsResolved && Array.isArray(photos) && photos.length > 0;

  return (
    <MotionProvider motion={motion}>
      <div className={`bg-bg-canvas min-h-screen flex flex-col ${STICKY_MARQUEE_SPACER_CLASS}`}>
        <Nav nav={nav} motion={motion} />
        <div className="sticky top-[72px] z-30 bg-bg-canvas border-b border-ink-primary/10" data-testid="page-back-bar">
          <div className="container-x py-3">
            <Link
              to="/"
              state={{ restoreScroll: true }}
              className="inline-flex items-center gap-2 text-ink-primary/70 hover:text-ink-primary font-body text-[13px] md:text-[14px] min-h-[44px] motion-safe:transition-colors motion-safe:duration-200 focus:outline-none focus-visible:underline"
              data-testid="page-back-link"
            >
              <ArrowLeft size={14} aria-hidden="true" /> Back to Home
            </Link>
          </div>
        </div>
        <main className="flex-1" data-testid="page-gallery">
          <section className="section-pad relative">
            <div className="container-x">
              <div ref={headerReveal.ref} className={`mt-2 text-center ${headerReveal.simple ? 'reveal-simple' : 'reveal-up'} ${headerReveal.revealed ? 'is-in' : ''}`}>
                <p className="eyebrow" data-testid="gallery-page-eyebrow">PAST EXPERIENCES.</p>
                <h1 className="mt-4 font-display font-bold uppercase text-ink-primary leading-[1.05] tracking-tightish text-[40px] md:text-[56px] lg:text-[72px]" data-testid="gallery-page-headline">
                  GALLERY
                </h1>
                <div className="mt-4 mx-auto w-16 h-[3px] bg-brand-orange" aria-hidden="true" />
                <p className="mt-5 font-body text-[14px] md:text-[15px] text-ink-primary/70 max-w-[640px] mx-auto leading-relaxed" data-testid="gallery-page-subhead">
                  Moments from past SEICon events.
                </p>
              </div>

              {/* Year / event filters. Rendered only when at least one
                  event has a visible photo — with zero events this block
                  does not exist and the page is identical to before. */}
              {!showPageError && settingsResolved && filters && Array.isArray(filters.years) && filters.years.length > 0 && (
                <div className="mt-10" data-testid="gallery-filters">
                  <div className="flex flex-wrap justify-center gap-2" data-testid="gallery-year-row">
                    <button
                      type="button"
                      onClick={() => applyFilter(null, null)}
                      className={`inline-flex items-center justify-center min-h-[40px] px-4 rounded-full border font-body text-[13px] font-semibold motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas ${selYear === null ? 'bg-ink-primary text-white border-ink-primary' : 'border-ink-primary/25 text-ink-primary hover:border-brand-orange'}`}
                      data-testid="gallery-filter-all"
                    >
                      All Photos
                    </button>
                    {filters.years.map((y) => (
                      <button
                        key={y.year}
                        type="button"
                        onClick={() => applyFilter(y.year, null)}
                        className={`inline-flex items-center justify-center min-h-[40px] px-4 rounded-full border font-body text-[13px] font-semibold motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas ${selYear === y.year ? 'bg-ink-primary text-white border-ink-primary' : 'border-ink-primary/25 text-ink-primary hover:border-brand-orange'}`}
                        data-testid="gallery-filter-year"
                      >
                        {y.year}
                      </button>
                    ))}
                  </div>
                  {selYear !== null && (() => {
                    const yearData = filters.years.find((y) => y.year === selYear);
                    if (!yearData || !Array.isArray(yearData.events) || yearData.events.length === 0) return null;
                    return (
                      <div className="mt-3 flex flex-wrap justify-center gap-2" data-testid="gallery-event-row">
                        <button
                          type="button"
                          onClick={() => applyFilter(selYear, null)}
                          className={`inline-flex items-center justify-center min-h-[36px] px-4 rounded-full border font-body text-[12px] font-semibold motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas ${!selEvent ? 'bg-brand-orange text-black border-brand-orange' : 'border-ink-primary/25 text-ink-primary hover:border-brand-orange'}`}
                          data-testid="gallery-filter-all-events"
                        >
                          All Events
                        </button>
                        {yearData.events.map((ev) => (
                          <button
                            key={ev.id}
                            type="button"
                            onClick={() => applyFilter(selYear, ev.id)}
                            className={`inline-flex items-center justify-center min-h-[36px] px-4 py-1.5 rounded-full border font-body text-[12px] font-semibold max-w-full sm:max-w-[320px] motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas ${selEvent === ev.id ? 'bg-brand-orange text-black border-brand-orange' : 'border-ink-primary/25 text-ink-primary hover:border-brand-orange'}`}
                            data-testid="gallery-filter-event"
                          >
                            {/* Long names WRAP — never truncate — so every
                                event stays readable on any screen. */}
                            <span className="whitespace-normal break-words text-center leading-snug">{ev.name}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}

              <div ref={gridReveal.ref} className="mt-12">
                {showPageError && (
                  <div className="text-center py-10" data-testid="gallery-page-error">
                    <p className="font-body text-ink-muted">We couldn't load this page. Please retry.</p>
                    <button
                      type="button"
                      onClick={retrySettings}
                      className="mt-4 inline-flex items-center justify-center min-h-[44px] px-4 rounded-sm bg-brand-orange text-black font-body text-[14px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas"
                      data-testid="gallery-page-error-retry"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {showLoading && (
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="gallery-loading">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="rounded-sm bg-ink-primary/10 animate-pulse" style={{ aspectRatio: '4/3' }} />
                    ))}
                  </div>
                )}

                {showGalleryError && (
                  <div className="text-center py-10" data-testid="gallery-error">
                    <p className="font-body text-ink-muted">We couldn't load the gallery. Please retry.</p>
                    <button
                      type="button"
                      onClick={retryGallery}
                      className="mt-4 inline-flex items-center justify-center min-h-[44px] px-4 rounded-sm bg-brand-orange text-black font-body text-[14px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas"
                      data-testid="gallery-error-retry"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {showEmpty && (
                  <p className="font-body text-ink-muted text-center py-10" data-testid="gallery-empty">
                    {selYear !== null || selEvent ? 'No photos for this selection yet.' : 'No photos in the gallery yet.'}
                  </p>
                )}

                {showGrid && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="gallery-page-grid">
                      {photos.map((g, i) => (
                        <button
                          key={g.id || i}
                          type="button"
                          onClick={() => openLightbox(i)}
                          className={`relative overflow-hidden rounded-sm bg-card-dark cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas ${gridReveal.simple ? 'reveal-simple' : 'reveal-card'} ${gridReveal.revealed ? 'is-in' : ''}`}
                          style={{ aspectRatio: '4/3', transitionDelay: gridReveal.revealed ? `${i * 60}ms` : '0ms' }}
                          aria-label={g.caption || `Open photo ${i + 1}`}
                          data-testid="gallery-page-tile"
                        >
                          <img
                            loading={i < EAGER_IMAGE_COUNT ? "eager" : "lazy"}
                            decoding="async"
                            src={fileUrl(g.image) || g.image}
                            alt={g.caption || 'Past experience'}
                            className="absolute inset-0 w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-500 hover:scale-[1.03]"
                          />
                        </button>
                      ))}
                    </div>
                    {hasMore && typeof nextCursor === 'string' && nextCursor.length > 0 && (
                      <div className="mt-8 text-center">
                        <button
                          type="button"
                          onClick={loadMore}
                          disabled={loadingMore}
                          className="inline-flex items-center justify-center min-h-[44px] px-6 rounded-sm bg-brand-orange text-black font-body text-[14px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas"
                          data-testid="gallery-load-more"
                        >
                          {loadingMore ? 'Loading…' : 'Load more'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        </main>
        <Footer footer={footer} motion={motion} presentingSponsor={settings.presenting_sponsor || null} />
        <StickyBottomMarquee bottomMarquee={bottomMarquee} />
      </div>

      {Array.isArray(photos) && photos.length > 0 && (
        <Lightbox
          photos={photos}
          initialIndex={lbIndex}
          isOpen={lbOpen}
          onClose={() => setLbOpen(false)}
          totalCount={galleryTotal}
          hasMore={hasMore}
          onRequestMore={loadMore}
          loadingMore={loadingMore}
        />
      )}
    </MotionProvider>
  );
}
